import React, { useState, useRef, useEffect } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Grid,
    IconButton,
    Paper,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    Assessment as AssessmentIcon,
    Psychology as PsychologyIcon,
    Refresh as RefreshIcon,
    Search as SearchIcon,
    Settings as SettingsIcon,
    ShoppingCart as ShoppingCartIcon,
    Error as ErrorIcon
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useNotify } from 'react-admin';
import { buildFilterObject, EventSearch, SearchEventTable, fetchHistoryPreFilteringRules, PreFilteringRule } from '../components/SearchEvents';
import MDateControl from '../../../components/controls/MDateControl';
import MSelect from '../../../components/controls/MSelect';
import MNumberRange from '../../../components/controls/MNumberRange';
import { dataProvider } from '../../../DataProvider';
import { loadSourceType, loadWhitelistCategory } from '../../../utils/common';
import MAutoComplete from '../../../components/controls/MAutoComplete';
import { SingleEventAnalysisTab } from '../components/tabs';
import { EventHistoryDashboard, EventItem } from '../models/event';
import { AIScoring, SingleEventAnalysisTabHandle } from '../components/tabs/utils/SingleEventAnalysisTab.types';
import PurchaseSeatsTab from './Components/PurchaseSeatsTab';
import { FilterValues, SeatRecommendation, AIPurchasedTicket } from '../models/seat';
import { AgGridHandle } from '../../../components/ag-grid/share/type';
import HistoryPrefilterConfigDialog from '../components/HistoryPrefilterConfigDialog';

// Tracking version options for filtering
export const TrackingVersion = [
    { id: '2', name: 'Version 2' },
    { id: 'ML', name: 'Version ML' },
    { id: 'DQ', name: 'Version DQ' },
    { id: 'NPS', name: 'Version NPS' },
    { id: 'SEC', name: 'Version SEC' }
];

const AnalysisHistorical = () => {
    const refSearchEvent = useRef<AgGridHandle>(null);
    const [activeTab, setActiveTab] = useState(0);
    const [historySearch, setHistorySearch] = useState<EventSearch>({
        dateFrom: new Date().toISOString(),
        dateTo: new Date(new Date().setFullYear(new Date().getFullYear() + 2)).toISOString(),
        recommendationDecision: '',
        simplifiedScoreFrom: 60,
        simplifiedScoreTo: 100,
        scoreFrom: 60,
        scoreTo: 100,
        liquidityConsistencyScoreFrom: 60,
        liquidityConsistencyScoreTo: 100
    });
    // History pre-filter configuration states
    const [historyPrefilteringRules, setHistoryPrefilteringRules] = useState<PreFilteringRule[]>([]);
    const [historyPrefilterDialogOpen, setHistoryPrefilterDialogOpen] = useState<boolean>(false);
    // state for filter in purchase seats tab
    const [purchaseSeatsFilter, setPurchaseSeatsFilter] = useState<FilterValues>({ removeIgnored: true, highRisk_eq: null });
    const [searchResults, setSearchResults] = useState<any>({ });
    const [selectedEvents, setSelectedEvents] = useState([]);
    const [marketTypes, setMarketTypes] = useState<any[]>([]);
    const [whitelistCategoryNames, setWhitelistCategoryNames] = useState<string[]>([]);
    // Single analysis states
    const [analysisTarget, setAnalysisTarget] = useState<EventHistoryDashboard | null>(null);
    const [selectedEvent, setSelectedEvent] = useState<EventItem | null>(null);
    const [seatRecommendations, setSeatRecommendations] = useState<any[]>([]);
    const [analysisCompleted, setAnalysisCompleted] = useState(false);
    const [aiScoring, setAiScoring] = useState<AIScoring | null>(null);

    const notify = useNotify();
    const singleAnalysisRef = useRef<SingleEventAnalysisTabHandle>(null);
    // Initial data loading states
    const [isLoadingInitialData, setIsLoadingInitialData] = useState(false);
    const [initialDataError, setInitialDataError] = useState<string | null>(null);
    const [initializing, setInitializing] = useState(true);
    const [initError, setInitError] = useState<string | null>(null);

    useEffect(() => {
        loadSourceType(dataProvider, (response: any) => {
            setMarketTypes(response);
        });
        loadWhitelistCategory(dataProvider, (response: any) => {
            let categoryNames: any[] = [...new Set(response.map((obj: any) => obj.name))];
            setWhitelistCategoryNames(categoryNames);
        });
        // Load initial cached data
        const loadData = async () => {
            try {
                setIsLoadingInitialData(true);
                setInitialDataError(null);
                // Call LoadCachedData endpoint to ensure cached data is loaded
                try {
                    await dataProvider.fetchData('RTP_Event', 'LoadCachedDataEndpoint');
                    notify('Cached data loaded successfully', { type: 'success' });
                    setInitializing(false);
                    setInitError(null);
                } catch (cacheError: any) {
                    notify('Error loading cached data: ' + (cacheError.message || 'Unknown error'), { type: 'error' });
                    // Continue loading other data even if cache load fails
                    setInitializing(false);
                    setInitError('Failed to load cached data: ' + (cacheError.message || 'Unknown error'));
                }
                finally {
                    setIsLoadingInitialData(false);
                }
            } catch (error: any) {
                console.error('Error loading initial data:', error);
                setInitialDataError(error.message || 'Failed to load initial data');
                setIsLoadingInitialData(false);
                notify('Error loading initial data: ' + (error.message || 'Unknown error'), { type: 'error' });
            }
        };

        loadData();
        // Fetch history pre-filtering rules from the backend config
        fetchHistoryPreFilteringRules().then(rules => {
            if (rules && rules.length > 0) {
                setHistoryPrefilteringRules(rules);
            }
        }).catch((error: any) => {
            console.error('Failed to load history pre-filtering rules:', error);
        });
    }, []);

    /**
     * Handles saving the history pre-filter configuration from the dialog.
     * Merges new rules into existing historyPrefilteringRules state.
     * @param {PreFilteringRule[]} newRules - Array of filter rules from the dialog
     */
    const handleReFilterSave = (newRules: PreFilteringRule[]): void => {
        const mergedRules = historyPrefilteringRules.map(rule => {
            const found = newRules.find(nr => nr.variable === rule.variable);
            if (found) {
                return { ...rule, value: found.value };
            }
            return rule;
        });
        // Add any new rules that were not in the previous set
        newRules.forEach(nr => {
            if (!mergedRules.find(r => r.variable === nr.variable)) {
                mergedRules.push(nr);
            }
        });
        setHistoryPrefilteringRules(mergedRules);
    };

    const searchHistoryData = async () => {
        try {
            let odataSearch = buildFilterObject(historySearch, historyPrefilteringRules);
            refSearchEvent.current?.customFilterSearch(odataSearch);
        } catch (error: any) {
            notify('Failed to search events: ' + (error.message || 'Unknown error'), { type: 'error' });
        }
    };
    /**
     * Updates search results and clears selected events.
     * Called by the AgGrid component when search results change.
     * @param {Array} result - Array of event objects from the search
     * @param {number} total - Total number of events matching the search
     * @param {number} pageIndex - Current page index
     * @param {number} pageSize - Number of events per page
     * @param {number} pageCount - Total page count
     */
    const updateSearchResult = (result: any, total: any, pageIndex: any, pageSize: any) => {
        setSearchResults({ data: result, total, pageIndex, pageSize, pageCount: Math.ceil(total / pageSize) });
        setSelectedEvents([]);
    };

    const handleEventSelectionChanged = (selectedRows: any) => {
        setSelectedEvents(selectedRows);
    };
    
    /**
     * Analyzes a single event using AI scoring and seat recommendation endpoints.
     * Updates selectedEvent, aiScoring, seatRecommendations, and analysisCompleted states.
     * Switches to Single Analysis tab.
     * @param {Object} event - The event object to analyze.
     * @async
     */
    const getSingleAnalysis = async (event: EventHistoryDashboard) => {
        setAnalysisTarget(event);
        let eventItem = EventItem.fromHistoryDashboard(event);
        setSelectedEvent(eventItem);
        // Switch to analysis tab
        setActiveTab(1);
    };

    // Effect to run analysis when analysisTarget is set
    useEffect(() => {
        // Fetch AI scoring and seat recommendations when analysisTarget is set
        if (activeTab === 1 && (analysisTarget || selectedEvent?.isPurchasedSeat) && singleAnalysisRef && singleAnalysisRef.current) {
            const runAnalysis = async () => {
                setAnalysisCompleted(false);
                try {
                    let analysisId = analysisTarget ? analysisTarget.latestAnalysisId ?? 0 : selectedEvent?.analysisId ?? 0;
                    // Fetch historical AI scoring data
                    const scoringData = singleAnalysisRef.current ? await singleAnalysisRef.current.fetchHistoricalScoringData(analysisId, analysisTarget ?? undefined) : {};
                    
                    // Extract velocitySnapshot, flattenedData from featuresJson and assign to velocityData
                    let flattenedData = {};
                    if (scoringData.featuresJson) {
                        try {
                            const features = JSON.parse(scoringData.featuresJson);
                            features?.velocitySnapshot && (scoringData.velocityData = features.velocitySnapshot);
                            features?.flattenedData && (flattenedData = features.flattenedData);
                        } catch (parseError) {
                            console.warn('Failed to parse featuresJson for velocity snapshot:', parseError);
                        }
                    }
                    
                    let { venueId, performerId, performerName } = scoringData;

                    // Create eventItem with merged flattenedData and suitable props for EventInfoCard
                    setSelectedEvent({ ...selectedEvent, ...flattenedData, venueId, performerId, performerName } as EventItem);
                    if (analysisTarget) {
                        // Also get seat recommendations from the original AI service if overallScore > threshold
                        const seatResponse = singleAnalysisRef.current ?
                            !scoringData.haveSeatAnalysis && (scoringData.overallEventScore || 0) > 60
                                ? await singleAnalysisRef.current.fetchSeatRecommendations(EventItem.fromHistoryDashboard(analysisTarget), analysisTarget.bulkAnalysisId)
                                : await singleAnalysisRef.current.getSeatRecommendationsHistory(analysisTarget.latestAnalysisId)
                            : [];
                        // Update state with seat recommendations for display in SingleAnalysisTab
                        setSeatRecommendations(seatResponse);
                        
                        // Mark analysis as completed to show results
                        setAnalysisCompleted(true);
                        notify('Loading data completed', { type: 'success' });
                    } else if (selectedEvent?.isPurchasedSeat) {
                        const seatResponse = singleAnalysisRef.current ?
                            await singleAnalysisRef.current.getSeatRecommendationsHistory(selectedEvent.analysisId ?? 0)
                            : [];
                        // Check selectedEvent.purchasedTicket if exist in seatResponse, then do nothing, else create SeatRecommendation from purchasedTicket
                        // and add to start of seatResponse
                        let purchasedTickets = selectedEvent.purchasedTicket ? SeatRecommendation.fromAIPurchasedTicket(selectedEvent.purchasedTicket as AIPurchasedTicket) : null;
                        let isExist = seatResponse.find(seat => {
                            return purchasedTickets && seat.row === purchasedTickets.row && seat.seatRange === purchasedTickets.seatRange && seat.section === purchasedTickets.section;
                        });
                        if (!isExist && purchasedTickets) {
                            seatResponse.unshift(purchasedTickets);
                        }
                        // Update state with seat recommendations for display in SingleAnalysisTab
                        setSeatRecommendations(seatResponse);
                        
                        // Mark analysis as completed to show results
                        setAnalysisCompleted(true);
                        notify('Loading data completed', { type: 'success' });
                    }
                } catch (error: any) {
                    setAiScoring(null);
                    setSeatRecommendations([]);
                    console.error('Real Ticket API analysis error:', error);
                    notify('Failed to analyze event with Real Ticket API: ' + error.message, { type: 'error' });
                } finally {
                    setAnalysisTarget(null);
                    // Block re-analysis when entering tab 1 again
                    if (selectedEvent?.isPurchasedSeat) {
                        setSelectedEvent(prev => ({ ...prev, isPurchasedSeat: false } as EventItem));
                    }
                }
            };

            runAnalysis();
        }
    }, [activeTab, analysisTarget]);


    // Show loading spinner while initial data is loading
    if (isLoadingInitialData) {
        return (
            <Box sx={{ width: '100%', p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                <Box sx={{ textAlign: 'center' }}>
                    <CircularProgress size={60} />
                    <Typography variant="h6" sx={{ mt: 2 }}>
                            Loading cached data and initializing analysis center...
                    </Typography>
                </Box>
            </Box>
        );
    }
    
    // Show error state if initial data loading failed
    if (initialDataError) {
        return (
            <Box sx={{ width: '100%', p: 3 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    <Typography variant="h6">Failed to load initial data</Typography>
                    <Typography>{initialDataError}</Typography>
                    <Button
                        variant="contained"
                        onClick={() => window.location.reload()}
                        sx={{ mt: 2 }}
                    >
                            Retry
                    </Button>
                </Alert>
            </Box>
        );
    }

    if (initializing) {
        return (
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '80vh',
                gap: 2
            }}>
                <CircularProgress size={60} />
                <Typography variant="h6" color="textSecondary">
                                    Loading AI Analysis System...
                </Typography>
                <Typography variant="body2" color="textSecondary">
                                    Initializing cached data and preparing the analysis engine
                </Typography>
            </Box>
        );
    }

    if (!initializing && initError) {
        return (
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '80vh',
                gap: 2,
                p: 3
            }}>
                <ErrorIcon color="error" sx={{ fontSize: 60 }} />
                <Typography variant="h6" color="error">
                                    Initialization Error
                </Typography>
                <Alert severity="error" sx={{ maxWidth: 600 }}>
                    {initError}
                </Alert>
                <Button
                    variant="contained"
                    onClick={() => window.location.reload()}
                    startIcon={<RefreshIcon />}
                >
                                    Refresh Page
                </Button>
            </Box>
        );
    }

    if (!initializing && !initError) {
        return (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Paper elevation={1} sx={{ p: 1.5, mb: 3, borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <PsychologyIcon sx={{ fontSize: 32, mr: 1.5, color: 'primary.main' }} />
                            <Box>
                                <Typography variant="h6" component="h2" sx={{ lineHeight: 1.2 }}>
                                    Analysis History
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    View historical event analysis records and perform new analyses
                                </Typography>
                            </Box>
                        </Box>
                        
                        {/* // Right side buttons */}
                        <Box>
                            <Tooltip title="Pre-filter Configuration">
                                <IconButton size="small" onClick={() => setHistoryPrefilterDialogOpen(true)}>
                                    <SettingsIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Refresh Data">
                                <IconButton size="small" onClick={() => window.location.reload()}>
                                    <RefreshIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>
                </Paper>
                <Card className='ai-analysis-tab'>
                    <Tabs
                        value={activeTab}
                        onChange={(e, newValue) => setActiveTab(newValue)}
                        variant="fullWidth"
                        sx={{ borderBottom: 1, borderColor: 'divider' }}
                    >
                        <Tab label="Historical Analysis Search" icon={<SearchIcon />} />
                        <Tab label="Single Analysis" icon={<AssessmentIcon />} />
                        <Tab label="Purchase Seats" icon={<ShoppingCartIcon />} />
                    </Tabs>

                    {activeTab === 0 && <Grid container spacing={3} key='event-search'>
                        <Grid size={2}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        Search History Analysis
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                        <Button
                                            variant="outlined"
                                            onClick={() => setHistoryPrefilterDialogOpen(true)}
                                            startIcon={<SettingsIcon />}
                                            size="small"
                                            fullWidth
                                        >
                                            Pre-filter Config
                                        </Button>
                                        <TextField
                                            label="Site Id"
                                            value={historySearch.siteId}
                                            onChange={(e) => setHistorySearch((prev: any) => ({ ...prev, siteId: e.target.value }))}
                                            onKeyDown={(e) => e.key === 'Enter' && searchHistoryData()}
                                            fullWidth
                                            size="small"
                                        />
                                        <TextField
                                            label="Event Name"
                                            value={historySearch.eventName}
                                            onChange={(e) => setHistorySearch((prev: any) => ({ ...prev, eventName: e.target.value }))}
                                            onKeyDown={(e) => e.key === 'Enter' && searchHistoryData()}
                                            fullWidth
                                            size="small"
                                        />
                                        <TextField
                                            label="Venue Name"
                                            value={historySearch.venue}
                                            onChange={(e) => setHistorySearch((prev: any) => ({ ...prev, venue: e.target.value }))}
                                            onKeyDown={(e) => e.key === 'Enter' && searchHistoryData()}
                                            fullWidth
                                            size="small"
                                        />
                                        <MSelect
                                            label={'Market Type'}
                                            value={historySearch.marketType}
                                            choices={marketTypes}
                                            onChange={(ev: any) => setHistorySearch((prev: any) => ({ ...prev, marketType: ev }))} />
                                        <MAutoComplete id="at_category_name" name='category_name' variant={'standard'} label={'Category Name'}
                                            choices={whitelistCategoryNames} className="" onChange={(values: any) => {
                                                console.log('value categoryname', values);
                                                setHistorySearch((prev: any) => ({ ...prev, categoryNames: values.length > 0 ? values.map((x:string) => x) : null }));
                                            }}/>
                                        <MDateControl
                                            label={'Date From'}
                                            defaultValue={historySearch.dateFrom}
                                            value={historySearch.dateFrom}
                                            onChange={(newValue: any) => setHistorySearch((prev: any) => ({ ...prev, dateFrom: newValue }))}
                                        />
                                        <MDateControl
                                            label={'Date To'}
                                            defaultValue={historySearch.dateTo}
                                            value={historySearch.dateTo}
                                            onChange={(newValue: any) => setHistorySearch((prev: any) => ({ ...prev, dateTo: newValue }))}
                                        />
                                        <MSelect
                                            label={'Recommendation'}
                                            value={historySearch.recommendationDecision}
                                            choices={['Strong Buy', 'Buy', 'Hold', 'Ignore']}
                                            onChange={(ev: any) => setHistorySearch((prev: any) => ({ ...prev, recommendationDecision: ev }))} />
                                        <MNumberRange
                                            name="simplifiedScore"
                                            label="Simplified Score"
                                            defaultValue={[historySearch.simplifiedScoreFrom, historySearch.simplifiedScoreTo]}
                                            onChange={(value: any, field: any) => {
                                                if (field === 'simplifiedScore_ge') {
                                                    setHistorySearch((prev: any) => ({ ...prev, simplifiedScoreFrom: value }));
                                                } else if (field === 'simplifiedScore_le') {
                                                    setHistorySearch((prev: any) => ({ ...prev, simplifiedScoreTo: value }));
                                                }
                                            }}
                                        />
                                        <MNumberRange
                                            name="liquidityConsistencyScore"
                                            label="LCS"
                                            defaultValue={[historySearch.liquidityConsistencyScoreFrom, historySearch.liquidityConsistencyScoreTo]}
                                            onChange={(value: any, field: any) => {
                                                if (field === 'liquidityConsistencyScore_ge') {
                                                    setHistorySearch((prev: any) => ({ ...prev, liquidityConsistencyScoreFrom: value }));
                                                } else if (field === 'liquidityConsistencyScore_le') {
                                                    setHistorySearch((prev: any) => ({ ...prev, liquidityConsistencyScoreTo: value }));
                                                }
                                            }}
                                        />
                                        <MNumberRange
                                            name="score"
                                            label="Score Range"
                                            defaultValue={[historySearch.scoreFrom, historySearch.scoreTo]}
                                            onChange={(value: any, field: any) => {
                                                if (field === 'score_ge') {
                                                    setHistorySearch((prev: any) => ({ ...prev, scoreFrom: value }));
                                                } else if (field === 'score_le') {
                                                    setHistorySearch((prev: any) => ({ ...prev, scoreTo: value }));
                                                }
                                            }}
                                        />
                                        <Button
                                            variant="contained"
                                            onClick={searchHistoryData}
                                            startIcon={<SearchIcon />}
                                        >
                                                Search
                                        </Button>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid size={10} sx={{ paddingTop: '20px', paddingBottom: '40px' }}>
                            <SearchEventTable
                                list="HistoryAnalysis"
                                filterValue={buildFilterObject(historySearch, historyPrefilteringRules)}
                                refInput={refSearchEvent}
                                analyzeEvent={getSingleAnalysis}
                                searchResults={searchResults.data}
                                paginationData={{ page: searchResults.pageIndex, pageSize: searchResults.pageSize, total: searchResults.total, pageCount: searchResults.pageCount }}
                                updateSearchResult={updateSearchResult}
                                onSelectionChanged={handleEventSelectionChanged}
                                selectedRowKeys={selectedEvents.map((x: any) => ({ id: x.id }))} />
                        </Grid>
                    </Grid>}

                    {activeTab === 1 && (
                        <SingleEventAnalysisTab
                            ref={singleAnalysisRef}
                            selectedEvent={selectedEvent}
                            setSelectedEvent={setSelectedEvent}
                            analysisCompleted={analysisCompleted}
                            aiScoring={aiScoring}
                            setAiScoring={setAiScoring}
                            seatRecommendations={seatRecommendations}
                            setSeatRecommendations={setSeatRecommendations}
                            setSearchResults={setSearchResults}
                        />
                    )}

                    {activeTab === 2 && (
                        <PurchaseSeatsTab
                            marketType={marketTypes}
                            filterValues={purchaseSeatsFilter}
                            setFilterValues={setPurchaseSeatsFilter}
                            setActiveTab={setActiveTab}
                            setSelectedEvent={setSelectedEvent}
                        />
                    )}
                </Card>
                <HistoryPrefilterConfigDialog
                    open={historyPrefilterDialogOpen}
                    onClose={() => setHistoryPrefilterDialogOpen(false)}
                    historyPrefilteringRules={historyPrefilteringRules}
                    onSave={handleReFilterSave}
                    marketTypes={marketTypes}
                />
            </LocalizationProvider>
        );
    }
};

interface AnalysisHistoricalPageConfig {
    name: string;
    list: React.FC;
}
const AnalysisHistoricalPage: AnalysisHistoricalPageConfig = {
    name: 'AnalysisHistorical',
    list: AnalysisHistorical
};

export default AnalysisHistoricalPage;
